import React, { useCallback, useEffect, useRef, useState } from "react";

import { Link } from "react-router-dom";

import useEmblaCarousel from "embla-carousel-react";

import Autoplay from "embla-carousel-autoplay";

import { motion } from "framer-motion";

import { ArrowUpRight } from "lucide-react";

import { ROUTES } from "@/lib/routes";



const TWEEN_FACTOR = 0.52;

const ROTATE_MAX = 38;



function normalizeDiff(diff) {

  if (diff > 0.5) return diff - 1;

  if (diff < -0.5) return diff + 1;

  return diff;

}



function PortfolioCard({ item, isActive }) {

  const videoRef = useRef(null);

  const videoSrc = item.video?.trim();

  const imageSrc = item.image?.trim();

  const showVideo = Boolean(videoSrc);



  useEffect(() => {

    const el = videoRef.current;

    if (!el || !showVideo) return undefined;

    if (isActive) {

      el.play().catch(() => {});

    } else {

      el.pause();

    }

    return undefined;

  }, [isActive, showVideo]);



  return (

    <div className="relative aspect-[3/4] overflow-hidden rounded-[1.75rem] bg-[#3e2723]/5 shadow-[0_24px_60px_-20px_rgba(62,39,35,0.35)] ring-1 ring-[#3e2723]/8 sm:rounded-[2rem] lg:rounded-[2.25rem]">

      {showVideo ? (

        <video

          ref={videoRef}

          src={videoSrc}

          poster={imageSrc || undefined}

          muted

          loop

          playsInline

          autoPlay={isActive}

          preload="metadata"

          className="absolute inset-0 h-full w-full object-cover"

        />

      ) : imageSrc ? (

        <img

          src={imageSrc}

          alt={item.name}

          className="absolute inset-0 h-full w-full object-cover"

          loading="lazy"

          draggable={false}

        />

      ) : (

        <div className="absolute inset-0 bg-gradient-to-br from-[#fbd9e5]/60 to-[#3e2723]/15" />

      )}

      <div

        className={`absolute inset-0 bg-gradient-to-t from-[#3e2723]/85 via-[#3e2723]/15 to-transparent transition-opacity duration-500 ${

          isActive ? "opacity-100" : "opacity-0"

        }`}

      />

      <div

        className={`absolute inset-x-0 bottom-0 p-5 transition-all duration-500 sm:p-6 ${

          isActive ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"

        }`}

      >

        {item.service ? (

          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#fbd9e5]/75">{item.service}</span>

        ) : null}

        {item.name ? (

          <p className="mt-1.5 font-display text-xl font-black tracking-tight text-[#fbd9e5] sm:text-2xl">{item.name}</p>

        ) : null}

      </div>

    </div>

  );

}



export default function CurvedPortfolioShowcase({ copy, items = [] }) {

  const autoplayPlugin = useRef(

    Autoplay({ delay: 4200, stopOnInteraction: false, stopOnMouseEnter: false })

  );

  const [emblaRef, emblaApi] = useEmblaCarousel(

    {

      loop: true,

      align: "center",

      containScroll: false,

      skipSnaps: false,

    },

    [autoplayPlugin.current]

  );

  const [selectedIndex, setSelectedIndex] = useState(0);



  const onSelect = useCallback(() => {

    if (!emblaApi) return;

    setSelectedIndex(emblaApi.selectedScrollSnap());

  }, [emblaApi]);



  const applyCurve = useCallback(() => {

    if (!emblaApi) return;

    const engine = emblaApi.internalEngine();

    const scrollProgress = emblaApi.scrollProgress();

    const slides = emblaApi.slideNodes();

    const snapList = emblaApi.scrollSnapList();

    const factor = TWEEN_FACTOR * snapList.length;



    snapList.forEach((snap, index) => {

      const slide = slides[index];

      if (!slide) return;



      let diffToTarget = snap - scrollProgress;



      if (engine.options.loop) {

        engine.slideLooper.loopPoints.forEach((loopItem) => {

          const target = loopItem.target();

          if (loopItem.index !== index || target === 0) return;

          if (target === -1) diffToTarget = snap - (1 + scrollProgress);

          if (target === 1) diffToTarget = snap + (1 - scrollProgress);

        });

      }



      diffToTarget = normalizeDiff(diffToTarget);

      const tween = 1 - Math.min(Math.abs(diffToTarget * factor), 1);

      const rotateY = diffToTarget * -ROTATE_MAX * (snapList.length > 1 ? 1 : 0);

      const scale = 0.78 + tween * 0.22;

      const opacity = 0.4 + tween * 0.6;



      slide.style.transform = `rotateY(${rotateY}deg) scale(${scale})`;

      slide.style.opacity = `${opacity}`;

      slide.style.zIndex = `${Math.round(tween * 100)}`;

    });

  }, [emblaApi]);



  useEffect(() => {

    if (!emblaApi) return undefined;

    onSelect();

    applyCurve();

    emblaApi.on("select", onSelect);

    emblaApi.on("reInit", onSelect);

    emblaApi.on("scroll", applyCurve);

    emblaApi.on("reInit", applyCurve);

    return () => {

      emblaApi.off("select", onSelect);

      emblaApi.off("reInit", onSelect);

      emblaApi.off("scroll", applyCurve);

      emblaApi.off("reInit", applyCurve);

    };

  }, [emblaApi, onSelect, applyCurve]);



  useEffect(() => {

    if (!emblaApi || items.length <= 1) return undefined;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    autoplayPlugin.current?.play?.();

    return undefined;

  }, [emblaApi, items.length]);



  const pillars = copy.pillars || [];



  return (

    <section className="relative overflow-x-clip bg-white pb-24 pt-28 sm:pb-28 sm:pt-36 lg:pb-36 lg:pt-40" data-testid="portfolio-showcase">

      <div

        aria-hidden

        className="pointer-events-none absolute left-1/2 top-0 h-[520px] w-[min(900px,120vw)] -translate-x-1/2 rounded-full bg-[#fbd9e5]/25 blur-3xl"

      />



      <div className="relative mx-auto max-w-4xl px-6 text-center sm:px-10">

        <motion.p

          initial={{ opacity: 0, y: 16 }}

          animate={{ opacity: 1, y: 0 }}

          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}

          className="text-[11px] font-bold uppercase tracking-[0.32em] text-[#3e2723]/55 sm:text-xs"

        >

          {copy.showcase_eyebrow || copy.eyebrow}

        </motion.p>

        <motion.h1

          initial={{ opacity: 0, y: 20 }}

          animate={{ opacity: 1, y: 0 }}

          transition={{ duration: 0.75, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}

          className="mt-4 font-display text-[2rem] font-black leading-[1.02] tracking-tighter text-[#3e2723] sm:text-5xl lg:text-[3.25rem]"

        >

          {copy.showcase_title || copy.title}

        </motion.h1>

        <motion.p

          initial={{ opacity: 0, y: 20 }}

          animate={{ opacity: 1, y: 0 }}

          transition={{ duration: 0.75, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}

          className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-[#7a5c55] sm:text-lg"

        >

          {copy.showcase_subtitle || copy.subtitle}

        </motion.p>

        <motion.div

          initial={{ opacity: 0, y: 20 }}

          animate={{ opacity: 1, y: 0 }}

          transition={{ duration: 0.75, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}

          className="mt-8 flex justify-center"

        >

          <Link

            to={ROUTES.contact}

            className="group inline-flex items-center gap-0 overflow-hidden rounded-full border border-[#3e2723]/12 bg-white pl-6 pr-1.5 py-1.5 text-sm font-semibold text-[#3e2723] shadow-[0_12px_40px_-16px_rgba(62,39,35,0.25)] transition hover:border-[#3e2723]/20 hover:shadow-[0_16px_48px_-16px_rgba(62,39,35,0.3)] sm:pl-7 sm:text-[15px]"

            data-testid="portfolio-showcase-cta"

          >

            <span>{copy.showcase_cta || copy.cta}</span>

            <span className="ml-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#3e2723] text-[#fbd9e5] transition duration-300 group-hover:scale-105 group-hover:bg-[#2a1a17]">

              <ArrowUpRight className="h-4 w-4 transition duration-300 group-hover:rotate-45" />

            </span>

          </Link>

        </motion.div>

      </div>



      {/* 3D curved gallery */}

      <div className="relative mt-14 pb-8 sm:mt-16 sm:pb-10 lg:mt-20 lg:pb-12">

        <div className="pointer-events-none absolute inset-x-0 top-1/2 z-10 h-px -translate-y-1/2 bg-gradient-to-r from-transparent via-[#3e2723]/8 to-transparent" aria-hidden />



        <div className="mx-auto max-w-[100rem] [perspective:1400px] [perspective-origin:center_center]">

          <div ref={emblaRef} className="overflow-hidden px-2 pb-14 sm:px-4 sm:pb-20 lg:pb-24">

            <div className="flex touch-pan-y [transform-style:preserve-3d]">

              {items.map((item, i) => (

                <div

                  key={`${item.name}-${i}`}

                  className="min-w-0 flex-[0_0_78%] pl-3 will-change-transform [transform-style:preserve-3d] sm:flex-[0_0_48%] sm:pl-4 lg:flex-[0_0_30%] xl:flex-[0_0_24%]"

                  data-testid={`portfolio-item-${i}`}

                >

                  <button

                    type="button"

                    onClick={() => emblaApi?.scrollTo(i)}

                    className="block w-full cursor-pointer text-left [backface-visibility:hidden]"

                    aria-label={item.name}

                  >

                    <PortfolioCard item={item} isActive={i === selectedIndex} />

                  </button>

                </div>

              ))}

            </div>

          </div>

        </div>



        {items.length > 1 ? (

          <div className="mt-6 flex justify-center px-6 sm:mt-8">

            <div className="inline-flex items-center gap-2.5 rounded-full border border-[#3e2723]/10 bg-[#faf8f8]/95 px-4 py-3 shadow-[0_10px_40px_-16px_rgba(62,39,35,0.22)] backdrop-blur-sm sm:gap-3 sm:px-6 sm:py-3.5">

              {items.map((_, i) => (

                <button

                  key={i}

                  type="button"

                  aria-label={`Proiect ${i + 1}`}

                  aria-current={i === selectedIndex ? "true" : undefined}

                  onClick={() => emblaApi?.scrollTo(i)}

                  className={`rounded-full transition-all duration-300 ${

                    i === selectedIndex

                      ? "h-2.5 w-10 bg-[#3e2723] shadow-sm sm:h-3 sm:w-14"

                      : "h-2.5 w-2.5 bg-[#3e2723]/25 hover:bg-[#3e2723]/45 sm:h-3 sm:w-3"

                  }`}

                />

              ))}

            </div>

          </div>

        ) : null}

      </div>



      {/* Process pillars */}

      {pillars.length > 0 ? (

        <motion.div

          initial={{ opacity: 0, y: 24 }}

          whileInView={{ opacity: 1, y: 0 }}

          viewport={{ once: true, margin: "-40px" }}

          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}

          className="mx-auto mt-16 max-w-6xl px-6 sm:mt-20 sm:px-10 lg:mt-24"

        >

          <div className="relative rounded-[2rem] border border-[#3e2723]/10 bg-gradient-to-b from-[#faf8f8] to-white p-6 shadow-[0_20px_60px_-24px_rgba(62,39,35,0.12)] sm:rounded-[2.25rem] sm:p-8 lg:p-10">

            <div

              aria-hidden

              className="pointer-events-none absolute left-[12%] right-[12%] top-[3.75rem] hidden h-px bg-gradient-to-r from-transparent via-[#3e2723]/15 to-transparent lg:block"

            />

            <div className="grid grid-cols-2 gap-8 sm:gap-10 lg:grid-cols-4 lg:gap-6">

              {pillars.map((pillar) => (

                <div key={pillar.n} className="relative flex flex-col items-center text-center lg:items-start lg:text-left">

                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#3e2723] text-[#fbd9e5] shadow-[0_12px_32px_-12px_rgba(62,39,35,0.45)] ring-4 ring-white sm:h-16 sm:w-16">

                    <span className="font-display text-base font-black tabular-nums sm:text-lg">#{pillar.n}</span>

                  </div>

                  <p className="mt-4 text-sm font-semibold leading-snug text-[#3e2723] sm:text-base">{pillar.title}</p>

                </div>

              ))}

            </div>

          </div>

        </motion.div>

      ) : null}

    </section>

  );

}


